import { useQueryClient } from '@tanstack/react-query'
import { BriefcaseBusiness, MapPin, Pencil } from 'lucide-react'
import { useContext, useState } from 'react'
import { toast } from 'sonner'

import EmployerContext from 'components/context/EmployerContext'
import { Badge, DropdownMenu } from 'slab'
import { getIndustryDisplayText } from 'utils/employer_utils'
import { getGetAdminEmployerProfilesEmployerProfileIdQueryKey, usePatchAdminEmployerProfilesEmployerProfileId } from 'utils/web_api'
import { AdminEmployerProfileIndustry } from 'utils/web_api.schemas'

import { CollapsibleDataContainer } from '../reusable/CollapsibleDataContainer'
import { CopyFieldRow } from '../reusable/CopyFieldRow'
import TableSkeleton from '../reusable/table/Skeleton'

const INDUSTRY_OPTIONS = Object.values(AdminEmployerProfileIndustry) as AdminEmployerProfileIndustry[]

export default function EmployerBasicInformation () {
  const { profile } = useContext(EmployerContext)
  const queryClient = useQueryClient()
  const [isUpdating, setIsUpdating] = useState(false)

  const patchProfile = usePatchAdminEmployerProfilesEmployerProfileId()

  const handleIndustryUpdate = async (industry: AdminEmployerProfileIndustry) => {
    if (!profile?.id) return

    setIsUpdating(true)
    try {
      await patchProfile.mutateAsync({
        employerProfileId: profile.id,
        data: { industry } as { industry: AdminEmployerProfileIndustry },
      })
      await queryClient.invalidateQueries({ queryKey: getGetAdminEmployerProfilesEmployerProfileIdQueryKey(profile.id) })
      toast.success('Industry updated')
    } catch {
      toast.error('Failed to update industry')
    } finally {
      setIsUpdating(false)
    }
  }

  const heading = (
    <div className='flex items-center space-x-2'>
      <span className='heading-md'>Basic information</span>
      {/* Industry Badge */}
      {profile?.industry && (<Badge type='info' leftIcon={BriefcaseBusiness}>{getIndustryDisplayText(profile?.industry)}</Badge>)}

      {/* State Badge */}
      {profile?.state && (<Badge type='success' leftIcon={MapPin}>{profile?.state}</Badge>)}
    </div>
  )

  const successContent = (
    <dl className='divide-y divide-gray-100 p-4'>
      <CopyFieldRow label='Name' value={profile?.name} />
      <CopyFieldRow label='DBA' value={profile?.dba} />
      <div className='px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0'>
        <dt className='text-sm font-medium leading-6 text-gray-900'>Industry</dt>
        <dd className='mt-1 text-sm leading-6 sm:col-span-2 sm:mt-0 flex items-center'>
          <span className={profile?.industry ? 'text-gray-700' : 'text-gray-400'}>
            {getIndustryDisplayText(profile?.industry) ?? '(none)'}
          </span>
          <DropdownMenu
            trigger={
              <button
                className='ml-2 p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50'
                disabled={isUpdating}
              >
                <Pencil size={14} />
              </button>
            }
          >
            <div className='max-h-64 overflow-y-auto'>
              {INDUSTRY_OPTIONS.map((industry) => (
                <DropdownMenu.Item key={industry} onSelect={() => handleIndustryUpdate(industry)}>
                  {getIndustryDisplayText(industry)}
                </DropdownMenu.Item>
              ))}
            </div>
          </DropdownMenu>
        </dd>
      </div>
      <CopyFieldRow label='Address Line 1' value={profile?.address_line1} />
      <CopyFieldRow label='Address Line 2' value={profile?.address_line2} />
      <CopyFieldRow label='City' value={profile?.city} />
      <CopyFieldRow label='State' value={profile?.state} />
      <CopyFieldRow label='ZIP' value={profile?.zip} />
      <CopyFieldRow label='Phone Number' value={profile?.phone_number} isPhoneNumber />
    </dl>
  )

  return (
    <CollapsibleDataContainer
      heading={heading}
      isLoading={!profile}
      error={!profile}
      localStorageKey='employerInformationOpenByDefault'
      loadingContent={<TableSkeleton rowCount={13} columnCount={3} />}
      errorContent={
        <div className='p-4 text-center text-red-500'>
          Error loading employer information
        </div>
      }
      successContent={successContent}
    />
  )
}
